import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import moment from 'moment';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import ErrorDisplay from './ErrorDisplay';
import DocumentViewerModal from './DocumentViewerModal';
import { roundUpToQuarter } from '../utils/priceRounding';

export default function PolicyFormModal({ isOpen, onClose, onSubmit, initialValues, clientId }) {
  const { t } = useLanguage();
  const { user } = useAuth();

  // State for car data
  const [carMakes, setCarMakes] = useState([]);
  const [carModels, setCarModels] = useState([]);
  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  
  // Error and submission state
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [draftPolicy, setDraftPolicy] = useState(null);
  const [startLater, setStartLater] = useState(false);

  // Generate years from current year back to 1990
  const currentYear = new Date().getFullYear();
  const carYears = Array.from({ length: currentYear - 1989 }, (_, i) => currentYear - i);

  // API functions
  const fetchCarMakes = async () => {
    setLoadingMakes(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/car-data/makes?active=true', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarMakes(response.data);
    } catch (error) {
      console.error('Error fetching car makes:', error);
    } finally {
      setLoadingMakes(false);
    }
  };

  const fetchCarModels = async (makeId) => {
    if (!makeId) {
      setCarModels([]);
      return;
    }
    
    setLoadingModels(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/car-data/makes/${makeId}/models?active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarModels(response.data);
    } catch (error) {
      console.error('Error fetching car models:', error);
      setCarModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const [formData, setFormData] = useState({
    // Personal Information (previously in Client)
    firstName: '',
    fatherName: '',
    grandfatherName: '',
    lastName: '',
    dateOfBirth: '',
    address: '',
    nationalId: '',
    mobile: '',
    email: '',
    nationality: '',
    passportNumber: '',
    licenseNumber: '',
    occupation: '',
    clientNumber: '',
    // Policy fields
    policyType: 'Car TPL',
    limitOfIndemnity: '25000', // Default value, typically set by admin
    premium: '',
    tax: 0,
    supervisionFees: 0,
    stampDuty: 1,
    issueFees: 10,
    totalPremium: '',
    premiumType: 'private',
    dailyRate: 4, // Default to private rate
    numberOfDays: 15,
    startDate: moment().format('YYYY-MM-DDTHH:mm'),
    endDate: moment().add(15, 'days').format('YYYY-MM-DDTHH:mm'),
    paymentStatus: 'unpaid',
    paymentMethod: '',
    notes: '',
    // Car insurance fields
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehiclePlate: '',
    chassisNumber: '',
    designatedDriver: '',
    designatedDriverLicense: '',
    carColour: '',
    enginePower: '',
    weight: '',
  });

  useEffect(() => {
    if (initialValues) {
      // If editing an existing policy
      setFormData({
        // Personal Information
        firstName: initialValues.firstName || '',
        fatherName: initialValues.fatherName || '',
        grandfatherName: initialValues.grandfatherName || '',
        lastName: initialValues.lastName || '',
        dateOfBirth: initialValues.dateOfBirth ? moment(initialValues.dateOfBirth).format('YYYY-MM-DD') : '',
        address: initialValues.address || '',
        nationalId: initialValues.nationalId || '',
        mobile: initialValues.mobile || '',
        email: initialValues.email || '',
        nationality: initialValues.nationality || '',
        passportNumber: initialValues.passportNumber || '',
        licenseNumber: initialValues.licenseNumber || '',
        occupation: initialValues.occupation || '',
        clientNumber: initialValues.clientNumber || '',
        // Policy fields
        policyType: initialValues.policyType || 'Car TPL',
        limitOfIndemnity: initialValues.limitOfIndemnity || '20000',
        premium: initialValues.premium || '',
        tax: initialValues.tax || 0,
        supervisionFees: initialValues.supervisionFees || 0,
        stampDuty: initialValues.stampDuty || 1,
        issueFees: initialValues.issueFees || 10,
        totalPremium: initialValues.totalPremium || '',
        premiumType: initialValues.premiumType || 'private',
        dailyRate: initialValues.premiumType === 'commercial' ? 5 : 4,
        numberOfDays: initialValues.numberOfDays || 15,
        startDate: moment(initialValues.startDate).format('YYYY-MM-DD'),
        endDate: moment(initialValues.endDate).format('YYYY-MM-DD'),
        paymentStatus: initialValues.paymentStatus || 'unpaid',
        paymentMethod: initialValues.paymentMethod || '',
        notes: initialValues.notes || '',
        chassisNumber: initialValues.chassisNumber || '',
        designatedDriver: initialValues.designatedDriver || '',
        designatedDriverLicense: initialValues.designatedDriverLicense || '',
        carColour: initialValues.carColour || '',
        enginePower: initialValues.enginePower || '',
        weight: initialValues.weight || '',
        // Spread policy details based on type
        ...initialValues.policyDetails
      });
      

    } else {
      // Reset form for new policy
      setFormData({
        // Personal Information
        firstName: '',
        fatherName: '',
        grandfatherName: '',
        lastName: '',
        dateOfBirth: '',
        address: '',
        nationalId: '',
        mobile: '',
        email: '',
        nationality: '',
        passportNumber: '',
        licenseNumber: '',
        occupation: '',
        clientNumber: '',
        // Policy fields
        policyType: 'Car TPL',
        limitOfIndemnity: '25000',
        premium: '',
        tax: 0,
        supervisionFees: 0,
        stampDuty: 1,
        issueFees: 10,
        totalPremium: '',
        premiumType: 'private',
        dailyRate: 4,
        numberOfDays: 15,
        startDate: moment().format('YYYY-MM-DDTHH:mm'),
        endDate: moment().add(15, 'days').format('YYYY-MM-DDTHH:mm'),
        paymentStatus: 'unpaid',
        paymentMethod: '',
        notes: '',
        vehicleMake: '',
        vehicleModel: '',
        vehicleYear: '',
        vehiclePlate: '',
        chassisNumber: '',
        designatedDriver: '',
        designatedDriverLicense: '',
        carColour: '',
        enginePower: '',
        weight: '',
      });
    }
  }, [initialValues, isOpen]);

  // Load car makes when modal opens and clear error
  useEffect(() => {
    if (isOpen) {
      setError(null);
      fetchCarMakes();
    }
  }, [isOpen]);

  // Load models when carMakes are loaded and we have a selected make (for editing)
  useEffect(() => {
    if (carMakes.length > 0 && formData.vehicleMake && carModels.length === 0) {
      const selectedMake = carMakes.find(make => make.name === formData.vehicleMake);
      if (selectedMake) {
        fetchCarModels(selectedMake.id);
      }
    }
  }, [carMakes, formData.vehicleMake]);

  // Calculate end date when start date or number of days changes
  useEffect(() => {
    if (formData.startDate && formData.numberOfDays) {
      const startDate = moment(formData.startDate);
      const calculatedEndDate = startDate.clone().add(formData.numberOfDays - 1, 'days');
      const today = moment();
      
      // If the calculated end date is today and we don't have a specific time, use current time
      // Otherwise, preserve the time from the start date
      let finalEndDate;
      if (calculatedEndDate.format('YYYY-MM-DD') === today.format('YYYY-MM-DD') && 
          (startDate.format('HH:mm') === '00:00' || !formData.endDate || moment(formData.endDate).format('HH:mm') === '00:00')) {
        finalEndDate = calculatedEndDate.hour(today.hour()).minute(today.minute()).format('YYYY-MM-DDTHH:mm');
      } else {
        // Preserve the time from start date
        finalEndDate = calculatedEndDate.hour(startDate.hour()).minute(startDate.minute()).format('YYYY-MM-DDTHH:mm');
      }
      
      setFormData(prev => ({ ...prev, endDate: finalEndDate }));
    }
  }, [formData.startDate, formData.numberOfDays]);

  // Calculate premium and total premium whenever relevant fields change
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = moment(formData.startDate);
      const end = moment(formData.endDate);
      const days = end.diff(start, 'days') + 1; // Include both start and end days
      
      if (days > 0) {
        // Calculate net premium
        const dailyRate = formData.premiumType === 'commercial' ? 5 : 4;
        let premium = days * dailyRate;
        // Round premium up to nearest 0.25 increment
        premium = roundUpToQuarter(premium);
        
        // Calculate tax (assuming 5% tax rate)
        let tax = premium * 0.05;
        // Round tax up to nearest 0.25 increment
        tax = roundUpToQuarter(tax);
        
        // Calculate supervision fees (50% of tax)
        let supervisionFees = tax * 0.5;
        // Round supervision fees up to nearest 0.25 increment
        supervisionFees = roundUpToQuarter(supervisionFees);
        
        // Calculate total premium using the formula
        let totalPremium = premium + tax + supervisionFees + parseFloat(formData.stampDuty || 0) + parseFloat(formData.issueFees || 0);
        // Round total premium up to nearest 0.25 increment
        totalPremium = roundUpToQuarter(totalPremium);
        
        setFormData(prev => ({ 
          ...prev, 
          premium: premium.toFixed(2), 
          dailyRate: dailyRate.toFixed(2), 
          numberOfDays: days,
          tax: tax.toFixed(2),
          supervisionFees: supervisionFees.toFixed(2),
          totalPremium: totalPremium.toFixed(2) 
        }));
      }
    }
  }, [formData.startDate, formData.endDate, formData.premiumType, formData.stampDuty, formData.issueFees]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'premiumType') {
      // Update daily rate when premium type changes
      const dailyRate = value === 'commercial' ? 5 : 4;
      setFormData(prev => ({ ...prev, [name]: value, dailyRate: dailyRate.toFixed(2) }));
    } else if (name === 'vehicleMake') {
      // When make changes, reset model to empty and update make
      setFormData(prev => ({ ...prev, [name]: value, vehicleModel: '' }));
      // Fetch models for the selected make
      if (value) {
        // Find the selected make object to get its ID
        const selectedMake = carMakes.find(make => make.name === value);
        if (selectedMake) {
          fetchCarModels(selectedMake.id);
        }
      } else {
        setCarModels([]);
      }
    } else if (name === 'startDate') {
      // Handle start date changes with smart time logic
      const selectedDate = moment(value);
      const today = moment();
      
      // If the selected date is today and no time was specified (ends with 00:00), use current time
      if (selectedDate.format('YYYY-MM-DD') === today.format('YYYY-MM-DD') && value.endsWith('00:00')) {
        const dateWithCurrentTime = selectedDate.hour(today.hour()).minute(today.minute()).format('YYYY-MM-DDTHH:mm');
        setFormData(prev => ({ ...prev, [name]: dateWithCurrentTime }));
      } else {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else if (name === 'numberOfDays') {
      // Validate minimum period (15 days)
      const days = parseInt(value);
      if (days < 15) {
        setFormData(prev => ({ ...prev, numberOfDays: 15 }));
      } else {
        setFormData(prev => ({ ...prev, [name]: days }));
      }
    } else if (name === 'endDate') {
      // Handle end date changes with smart time logic
      const selectedDate = moment(value);
      const today = moment();
      let finalValue = value;
      
      // If the selected date is today and no time was specified (ends with 00:00), use current time
      if (selectedDate.format('YYYY-MM-DD') === today.format('YYYY-MM-DD') && value.endsWith('00:00')) {
        finalValue = selectedDate.hour(today.hour()).minute(today.minute()).format('YYYY-MM-DDTHH:mm');
      }
      
      // Update end date directly and recalculate number of days
      const startDate = moment(formData.startDate);
      const endDate = moment(finalValue);
      const days = endDate.diff(startDate, 'days') + 1;
      
      if (days < 15) {
        // If selected end date creates less than 15 days, set to minimum
        const newEndDate = moment(startDate).add(14, 'days').format('YYYY-MM-DDTHH:mm');
        setFormData(prev => ({ ...prev, endDate: newEndDate, numberOfDays: 15 }));
      } else {
        setFormData(prev => ({ ...prev, [name]: finalValue, numberOfDays: days }));
      }
    } else if (['premium', 'tax', 'supervisionFees', 'stampDuty', 'issueFees', 'totalPremium'].includes(name)) {
      // Format numbers to 2 decimal places
      const formattedValue = Number(value).toFixed(2);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const prepareSubmissionData = () => {
    // Get branch and company IDs from authenticated user
    const branchId = user?.branchId;
    const companyId = user?.companyId;
    
    return {
      id: draftPolicy?.id || initialValues?.id,
      // Branch and company information (REQUIRED)
      branchId,
      companyId,
      // Personal Information
      firstName: formData.firstName,
      fatherName: formData.fatherName,
      grandfatherName: formData.grandfatherName,
      lastName: formData.lastName,
      dateOfBirth: formData.dateOfBirth,
      address: formData.address,
      nationalId: formData.nationalId,
      mobile: formData.mobile,
      email: formData.email,
      nationality: formData.nationality,
      passportNumber: formData.passportNumber,
      licenseNumber: formData.licenseNumber,
      occupation: formData.occupation,
      clientNumber: formData.clientNumber,
      // Policy fields
      policyType: formData.policyType,
      limitOfIndemnity: formData.limitOfIndemnity,
      premium: formData.premium,
      tax: formData.tax,
      supervisionFees: formData.supervisionFees,
      stampDuty: formData.stampDuty,
      issueFees: formData.issueFees,
      totalPremium: formData.totalPremium,
      dailyRate: formData.dailyRate,
      premiumType: formData.premiumType,
      chassisNumber: formData.chassisNumber,
      designatedDriver: formData.designatedDriver,
      designatedDriverLicense: formData.designatedDriverLicense,
      numberOfDays: formData.numberOfDays,
      startDate: startLater ? formData.startDate : new Date().toISOString(),
      endDate: formData.endDate,
      paymentStatus: formData.paymentStatus,
      paymentMethod: formData.paymentStatus === 'paid' ? formData.paymentMethod : null,
      notes: formData.notes,
      carColour: formData.carColour,
      enginePower: formData.enginePower,
      weight: formData.weight,
      vehicleMake: formData.vehicleMake,
      vehicleModel: formData.vehicleModel,
      vehicleYear: formData.vehicleYear,
      vehiclePlate: formData.vehiclePlate,
      policyDetails: {},
    };
  };

  const handleSaveDraft = async () => {
    setError(null);
    setIsSavingDraft(true);
    
    try {
      const token = localStorage.getItem('token');
      const submissionData = prepareSubmissionData();
      
      const response = await api.post(
        '/api/policies/draft',
        submissionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDraftPolicy(response.data.policy);
      toast.success(t('policy.draftSaved'));
    } catch (draftError) {
      console.error('Draft save error:', draftError);
      setError(draftError);
      toast.error(t('policy.draftSaveFailed'));
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePreview = async () => {
    // Must save as draft first before preview
    if (!draftPolicy) {
      await handleSaveDraft();
    }
    setIsPreviewOpen(true);
  };

  const handleActivate = async () => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      if (!draftPolicy) {
        throw new Error('Please save as draft first');
      }
      
      if (formData.paymentStatus !== 'paid') {
        throw new Error(t('policy.paymentRequired'));
      }
      
      const token = localStorage.getItem('token');
      const response = await api.post(
        `/api/policies/${draftPolicy.id}/activate`,
        {
          startDate: startLater ? formData.startDate : new Date().toISOString(),
          paymentMethod: formData.paymentMethod
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(t('policy.policyActivated'));
      handleClose();
      if (onSubmit) {
        onSubmit(response.data.policy);
      }
    } catch (activateError) {
      console.error('Activation error:', activateError);
      setError(activateError);
      toast.error(activateError.response?.data?.message || t('policy.activationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent default form submission - use the 3 buttons instead
  };

  // Renders fields for Car TPL
  const renderCarFields = () => {
    return (
      <>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="vehicleMake" className="block text-sm font-medium text-gray-700">
            {t('policy.vehicleMake')}
          </label>
          <select
            name="vehicleMake"
            id="vehicleMake"
            value={formData.vehicleMake}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            required
            disabled={loadingMakes}
          >
            <option value="">
              {loadingMakes ? t('common.loading') : t('policy.selectMake')}
            </option>
            {carMakes.map((make) => (
              <option key={make.id} value={make.name}>
                {make.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="vehicleModel" className="block text-sm font-medium text-gray-700">
            {t('policy.vehicleModel')}
          </label>
          <select
            name="vehicleModel"
            id="vehicleModel"
            value={formData.vehicleModel}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            required
            disabled={!formData.vehicleMake || loadingModels}
          >
            <option value="">
              {loadingModels ? t('common.loading') : t('policy.selectModel')}
            </option>
            {carModels.map((model) => (
              <option key={model.id} value={model.name}>
                {model.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="vehicleYear" className="block text-sm font-medium text-gray-700">
            {t('policy.vehicleYear')}
          </label>
          <select
            name="vehicleYear"
            id="vehicleYear"
            value={formData.vehicleYear}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            required
          >
            <option value="">{t('policy.selectYear')}</option>
            {carYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="vehiclePlate" className="block text-sm font-medium text-gray-700">
            {t('policy.licensePlate')}
          </label>
          <input
            type="text"
            name="vehiclePlate"
            id="vehiclePlate"
            value={formData.vehiclePlate}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            required
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="chassisNumber" className="block text-sm font-medium text-gray-700">
            {t('policy.chassisNumber')}
          </label>
          <input
            type="text"
            name="chassisNumber"
            id="chassisNumber"
            value={formData.chassisNumber}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="designatedDriver" className="block text-sm font-medium text-gray-700">
            {t('policy.designatedDriver')}
          </label>
          <input
            type="text"
            name="designatedDriver"
            id="designatedDriver"
            value={formData.designatedDriver}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="designatedDriverLicense" className="block text-sm font-medium text-gray-700">
            {t('policy.driverLicenseNumber')}
          </label>
          <input
            type="text"
            name="designatedDriverLicense"
            id="designatedDriverLicense"
            value={formData.designatedDriverLicense}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="carColour" className="block text-sm font-medium text-gray-700">
            {t('policy.carColour')}
          </label>
          <input
            type="text"
            name="carColour"
            id="carColour"
            value={formData.carColour}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            required
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="enginePower" className="block text-sm font-medium text-gray-700">
            {t('policy.enginePower')}
          </label>
          <input
            type="text"
            name="enginePower"
            id="enginePower"
            value={formData.enginePower}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            required
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="weight" className="block text-sm font-medium text-gray-700">
            {t('policy.weight')}
          </label>
          <input
            type="text"
            name="weight"
            id="weight"
            value={formData.weight}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            required
          />
        </div>
      </>
    );
  };

  return (
    <>
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={handleClose}
                  >
                    <span className="sr-only">{t('common.close')}</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                      {initialValues ? t('policy.editPolicy') : t('policy.addPolicy')}
                    </Dialog.Title>

                    {error && (
                      <ErrorDisplay 
                        error={error} 
                        onDismiss={() => setError(null)} 
                        className="mt-4 mb-4"
                      />
                    )}

                    <form onSubmit={handleSubmit} className="mt-6">
                      <div className="grid grid-cols-6 gap-6">
                        {/* Personal Information Section */}
                        <div className="col-span-6">
                          <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                            {t('policies.personalInformation')}
                          </h3>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                            {t('policies.firstName')} *
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            id="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="fatherName" className="block text-sm font-medium text-gray-700">
                            {t('policies.fatherName')} *
                          </label>
                          <input
                            type="text"
                            name="fatherName"
                            id="fatherName"
                            value={formData.fatherName}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="grandfatherName" className="block text-sm font-medium text-gray-700">
                            {t('policies.grandfatherName')} *
                          </label>
                          <input
                            type="text"
                            name="grandfatherName"
                            id="grandfatherName"
                            value={formData.grandfatherName}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                            {t('policies.lastName')} *
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            id="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                            {t('policies.dateOfBirth')} *
                          </label>
                          <input
                            type="date"
                            name="dateOfBirth"
                            id="dateOfBirth"
                            value={formData.dateOfBirth}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="nationality" className="block text-sm font-medium text-gray-700">
                            {t('policies.nationality')} *
                          </label>
                          <input
                            type="text"
                            name="nationality"
                            id="nationality"
                            value={formData.nationality}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="nationalId" className="block text-sm font-medium text-gray-700">
                            {t('policies.nationalId')}
                          </label>
                          <input
                            type="text"
                            name="nationalId"
                            id="nationalId"
                            value={formData.nationalId}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="passportNumber" className="block text-sm font-medium text-gray-700">
                            {t('policies.passportNumber')} *
                          </label>
                          <input
                            type="text"
                            name="passportNumber"
                            id="passportNumber"
                            value={formData.passportNumber}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700">
                            {t('policies.licenseNumber')} *
                          </label>
                          <input
                            type="text"
                            name="licenseNumber"
                            id="licenseNumber"
                            value={formData.licenseNumber}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">
                            {t('policies.mobile')} *
                          </label>
                          <input
                            type="tel"
                            name="mobile"
                            id="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            {t('policies.email')}
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                            {t('policies.address')} *
                          </label>
                          <input
                            type="text"
                            name="address"
                            id="address"
                            value={formData.address}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        {/* Optional fields hidden - not required */}

                        {/* Policy Information Section */}
                        <div className="col-span-6 mt-6">
                          <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                            {t('policies.policyInformation')}
                          </h3>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="policyType" className="block text-sm font-medium text-gray-700">
                            {t('policy.policyType')}
                          </label>
                          <select 
                            name="policyType"
                            id="policyType"
                            value={formData.policyType}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          >
                            <option value="Car TPL">Car TPL</option>
                            <option value="Mandatory Car Insurance">Mandatory Car Insurance</option>
                          </select>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="premiumType" className="block text-sm font-medium text-gray-700">
                            {t('policy.premiumType')}
                          </label>
                          <select
                            name="premiumType"
                            id="premiumType"
                            value={formData.premiumType}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          >
                            <option value="private">{t('policy.private')}</option>
                            <option value="commercial">{t('policy.commercial')}</option>
                          </select>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="limitOfIndemnity" className="block text-sm font-medium text-gray-700">
                            {t('policy.limitOfIndemnity')}
                          </label>
                          <input
                            type="number"
                            name="limitOfIndemnity"
                            id="limitOfIndemnity"
                            value={formData.limitOfIndemnity}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            readOnly
                          />
                          <p className="mt-1 text-xs text-gray-500">{t('policy.setByAdmin')}</p>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                            {t('policy.startDate')}
                          </label>
                          <input
                            type="datetime-local"
                            name="startDate"
                            id="startDate"
                            value={formData.startDate}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-2">
                          <label htmlFor="numberOfDays" className="block text-sm font-medium text-gray-700">
                            {t('policy.numberOfDays')}
                          </label>
                          <input
                            type="number"
                            name="numberOfDays"
                            id="numberOfDays"
                            value={formData.numberOfDays}
                            onChange={handleChange}
                            min="15"
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">{t('policy.minimumDays')}</p>
                        </div>

                        <div className="col-span-6 sm:col-span-4">
                          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                            {t('policy.endDate')}
                          </label>
                          <input
                            type="datetime-local"
                            name="endDate"
                            id="endDate"
                            value={formData.endDate}
                            onChange={handleChange}
                            min={formData.startDate ? moment(formData.startDate).add(14, 'days').format('YYYY-MM-DDTHH:mm') : ''}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        {/* Premium calculation fields */}
                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="premium" className="block text-sm font-medium text-gray-700">
                            {t('policy.netPremium')}
                          </label>
                          <input
                            type="number"
                            name="premium"
                            id="premium"
                            value={formData.premium}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {formData.dailyRate} LYD/day × {formData.numberOfDays} days
                          </p>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="tax" className="block text-sm font-medium text-gray-700">
                            {t('policy.tax')}
                          </label>
                          <input
                            type="number"
                            name="tax"
                            id="tax"
                            value={formData.tax}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="supervisionFees" className="block text-sm font-medium text-gray-700">
                            {t('policy.supervisionFees')}
                          </label>
                          <input
                            type="number"
                            name="supervisionFees"
                            id="supervisionFees"
                            value={formData.supervisionFees}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500">{t('policy.percentOfTax')}</p>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="stampDuty" className="block text-sm font-medium text-gray-700">
                            {t('policy.stampDuty')}
                          </label>
                          <input
                            type="number"
                            name="stampDuty"
                            id="stampDuty"
                            value={formData.stampDuty}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500">{t('policy.fixedStampDuty')}</p>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="issueFees" className="block text-sm font-medium text-gray-700">
                            {t('policy.issueFees')}
                          </label>
                          <input
                            type="number"
                            name="issueFees"
                            id="issueFees"
                            value={formData.issueFees}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                          <p className="mt-1 text-xs text-gray-500">{t('policy.fixedIssueFees')}</p>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="totalPremium" className="block text-sm font-medium text-gray-700">
                            {t('policy.totalPremium')}
                          </label>
                          <input
                            type="number"
                            name="totalPremium"
                            id="totalPremium"
                            value={formData.totalPremium}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="paymentStatus" className="block text-sm font-medium text-gray-700">
                            {t('policy.paymentStatus')}
                          </label>
                          <select
                            name="paymentStatus"
                            id="paymentStatus"
                            value={formData.paymentStatus}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          >
                            <option value="unpaid">{t('policy.unpaid')}</option>
                            <option value="paid">{t('policy.paid')}</option>
                          </select>
                        </div>

                        {formData.paymentStatus === 'paid' && (
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700">
                              {t('policy.paymentMethod')}
                            </label>
                            <select
                              name="paymentMethod"
                              id="paymentMethod"
                              value={formData.paymentMethod}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            >
                              <option value="">{t('policy.selectPaymentMethod')}</option>
                              <option value="cash">{t('policy.cash')}</option>
                              <option value="cheque">{t('policy.cheque')}</option>
                              <option value="onAccount">{t('policy.onAccount')}</option>
                            </select>
                          </div>
                        )}

                        {/* Car TPL specific fields */}
                        {renderCarFields()}

                        <div className="col-span-6">
                          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                            {t('policy.notes')}
                          </label>
                          <textarea
                            name="notes"
                            id="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            rows={3}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>
                      </div>

                      {/* Start Later Checkbox for Car TPL */}
                      {formData.policyType === 'Car TPL' && (
                        <div className="col-span-6">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={startLater}
                              onChange={(e) => setStartLater(e.target.checked)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">{t('policy.startPolicyLater')}</span>
                          </label>
                        </div>
                      )}

                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-4 sm:gap-3">
                        {/* Save as Draft Button */}
                        <button
                          type="button"
                          onClick={handleSaveDraft}
                          className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          disabled={isSavingDraft || isSubmitting}
                        >
                          {isSavingDraft ? t('policy.saving') : t('policy.saveDraft')}
                        </button>

                        {/* Preview Button */}
                        <button
                          type="button"
                          onClick={handlePreview}
                          className="w-full inline-flex justify-center rounded-md border border-blue-300 shadow-sm px-4 py-2 bg-blue-50 text-base font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          disabled={isSavingDraft || isSubmitting}
                        >
                          {t('policy.preview')}
                        </button>

                        {/* Activate/Create Button (اصدار) */}
                        <button
                          type="button"
                          onClick={handleActivate}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                          disabled={!draftPolicy || formData.paymentStatus !== 'paid' || isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {t('policy.activating')}
                            </>
                          ) : (
                            t('policy.activate')
                          )}
                        </button>

                        {/* Cancel Button */}
                        <button
                          type="button"
                          className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          onClick={handleClose}
                          disabled={isSubmitting || isSavingDraft}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>

    {/* Document Viewer Modal for Preview */}
    {isPreviewOpen && draftPolicy && (
      <DocumentViewerModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        policyId={draftPolicy.id}
        documentType="html"
      />
    )}
    </>
  );
} 